
import React from 'react';
import PageLayout from '@/components/layout/PageLayout';
import PageHero from '@/components/ui/PageHero';
import ScrollReveal from '@/components/ui/ScrollReveal';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, Monitor, Camera, PenTool, BarChart3, Code, Home, Mic, Video, Lightbulb } from 'lucide-react';
import ServiceCard from '@/components/ui/ServiceCard';

const Services = () => {
  const mainServices = [
    {
      icon: <Monitor />,
      title: 'Website Design & Development',
      description: 'Custom websites designed for conversion and user experience, built with modern technologies and optimized for performance.',
      badge: 'Web',
      link: '/services/website-design',
      featured: 'Contact for custom website quote'
    },
    {
      icon: <Camera />,
      title: 'Professional Photography',
      description: 'High-quality photography services for various industries including real estate, fashion, food, events, and lifestyle brands.',
      badge: 'Visual',
      link: '/services/photography',
      featured: 'Contact for photography pricing'
    },
    {
      icon: <BarChart3 />,
      title: 'Digital Marketing',
      description: 'Comprehensive marketing strategies including social media management, content creation, and paid advertising campaigns.',
      badge: 'Marketing',
      link: '/services/marketing',
      featured: 'Contact for marketing packages'
    },
    {
      icon: <PenTool />,
      title: 'Branding & Identity',
      description: 'Brand development including logo design, messaging, and creating a cohesive identity that resonates with your audience.',
      badge: 'Branding',
      link: '/services/branding',
      featured: 'Contact for branding services'
    },
    {
      icon: <Code />,
      title: 'Software Solutions',
      description: 'Custom software, CRM systems, and all-in-one platforms designed to streamline operations and enhance business efficiency.',
      badge: 'Technology',
      link: '/services/software',
      featured: 'Contact for software pricing'
    },
    {
      icon: <Home />,
      title: 'Real Estate Media',
      description: 'Specialized media services for real estate professionals including photography, virtual tours, and promotional content.',
      badge: 'Real Estate',
      link: '/services/real-estate-media',
      featured: 'Contact for real estate media packages'
    },
    {
      icon: <Mic />,
      title: 'Tech Coaching',
      description: 'Personalized coaching for podcast creation, AI implementation, automation, and digital transformation for businesses.',
      badge: 'Coaching',
      link: '/services/tech-coaching',
      featured: 'Contact for coaching rates'
    },
    {
      icon: <Video />,
      title: 'Virtual Tours',
      description: 'Custom 360° immersive virtual tours for businesses, real estate, hospitality, and more. Better than Matterport with affordable pricing.',
      badge: 'Interactive',
      link: '/services/virtual-tours',
      featured: 'Contact for virtual tour quotes'
    },
    {
      icon: <Lightbulb />,
      title: 'LED Printing',
      description: 'Revolutionary LED printing technology that embeds ultra-thin, flexible light-emitting diodes directly into various materials.',
      badge: 'Innovation',
      link: '/services/led-printing',
      featured: 'Contact for custom quote'
    }
  ];

  // SEO optimized title and description for Services page
  const pageTitle = "Professional Services | Baton Rouge, LA | Astrem";
  const pageDescription = "Comprehensive web design, photography, marketing, and tech solutions in Baton Rouge, LA. Astrem delivers expert results for businesses of all sizes. Schedule your consultation today!";

  return (
    <PageLayout 
      title={pageTitle}
      description={pageDescription}
      ogImage="https://astrem.co/lovable-uploads/a1afc3e0-0836-4e40-9b00-14ef27c65e43.png"
      customCta="Schedule your consultation today!"
    >
      <PageHero 
        title="Our Services" 
        subtitle="Comprehensive Solutions for Your Needs"
        description="From web design and photography to marketing and innovative technology solutions, we offer a wide range of services to help businesses and individuals succeed in the digital age."
        ctaText="Contact Us"
        ctaLink="/contact"
        backgroundImage="https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=1000&auto=format&fit=crop"
      />

      {/* Services Overview Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                What We Offer
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">Comprehensive Service Offerings</h2>
              <p className="text-lg text-astrem-gray-600">
                We provide a diverse range of services designed to meet the unique needs of businesses and individuals. Each service is tailored to deliver exceptional results and help you achieve your goals.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Web & Digital Services */}
      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto mb-12">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Digital Services
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">Web & Digital Solutions</h2>
              <p className="text-lg text-astrem-gray-600">
                Establish a strong online presence with our web design, development, and digital marketing services.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mainServices.slice(0, 3).map((service, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow relative h-full">
                  <div className="absolute top-4 right-4 z-10">
                    <span className="inline-block px-3 py-1 bg-primary text-white rounded-full text-sm font-medium">
                      {service.badge}
                    </span>
                  </div>
                  <div className="p-6">
                    <div className="w-12 h-12 bg-astrem-gray-100 rounded-full flex items-center justify-center mb-4">
                      <div className="text-astrem-black">
                        {service.icon}
                      </div>
                    </div>
                    <h3 className="text-xl font-medium mb-3">{service.title}</h3>
                    <p className="text-astrem-gray-600 mb-4">{service.description}</p>
                    <div className="bg-astrem-gray-50 rounded-md p-3 mb-4">
                      <p className="text-sm font-medium text-primary">
                        {service.featured}
                      </p>
                    </div>
                    <Button asChild variant="outline" className="w-full rounded-full">
                      <Link to={service.link}>
                        Learn More
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Branding & Creative Services */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto mb-12">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Creative Services
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">Branding & Visual Identity</h2>
              <p className="text-lg text-astrem-gray-600">
                Stand out from the competition with compelling visual content and cohesive brand identity.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mainServices.slice(3, 6).map((service, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow relative h-full">
                  <div className="absolute top-4 right-4 z-10">
                    <span className="inline-block px-3 py-1 bg-primary text-white rounded-full text-sm font-medium">
                      {service.badge}
                    </span>
                  </div>
                  <div className="p-6">
                    <div className="w-12 h-12 bg-astrem-gray-100 rounded-full flex items-center justify-center mb-4">
                      <div className="text-astrem-black">
                        {service.icon}
                      </div>
                    </div>
                    <h3 className="text-xl font-medium mb-3">{service.title}</h3>
                    <p className="text-astrem-gray-600 mb-4">{service.description}</p>
                    <div className="bg-astrem-gray-50 rounded-md p-3 mb-4">
                      <p className="text-sm font-medium text-primary">
                        {service.featured}
                      </p>
                    </div>
                    <Button asChild variant="outline" className="w-full rounded-full">
                      <Link to={service.link}>
                        Learn More
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Technology & Innovation Services */}
      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto mb-12">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Technology Services
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">Innovative Solutions</h2>
              <p className="text-lg text-astrem-gray-600">
                Leverage cutting-edge technology to enhance your business operations and customer experiences.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mainServices.slice(6, 9).map((service, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow relative h-full">
                  <div className="absolute top-4 right-4 z-10">
                    <span className="inline-block px-3 py-1 bg-primary text-white rounded-full text-sm font-medium">
                      {service.badge}
                    </span>
                  </div>
                  <div className="p-6">
                    <div className="w-12 h-12 bg-astrem-gray-100 rounded-full flex items-center justify-center mb-4">
                      <div className="text-astrem-black">
                        {service.icon}
                      </div>
                    </div>
                    <h3 className="text-xl font-medium mb-3">{service.title}</h3>
                    <p className="text-astrem-gray-600 mb-4">{service.description}</p>
                    <div className="bg-astrem-gray-50 rounded-md p-3 mb-4">
                      <p className="text-sm font-medium text-primary">
                        {service.featured}
                      </p>
                    </div>
                    <Button asChild variant="outline" className="w-full rounded-full">
                      <Link to={service.link}>
                        Learn More
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Solutions Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div>
                <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                  Tailored Solutions
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Don't See Exactly What You Need?
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-8">
                  We specialize in creating custom solutions tailored to your specific needs. Our diverse skill set allows us to tackle unique challenges and develop innovative approaches for projects that don't fit neatly into predefined categories.
                </p>
                <p className="text-lg text-astrem-gray-600 mb-8">
                  Whether you need a combination of our services or something entirely different, we're here to help. Contact us to discuss your project, and we'll work together to find the perfect solution.
                </p>
                <Button asChild className="rounded-full">
                  <Link to="/contact">
                    Get a Custom Quote
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <img
                src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=1000&auto=format&fit=crop"
                alt="Custom Solutions"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-astrem-black text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <ScrollReveal>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                Ready to Get Started?
              </h2>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <p className="text-lg text-astrem-gray-300 mb-8">
                Contact us today to discuss your project and learn how our services can help you achieve your goals. We're committed to delivering exceptional results that exceed your expectations.
              </p>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <Button asChild size="lg" className="bg-white text-astrem-black hover:bg-astrem-gray-100 rounded-full px-8">
                <Link to="/contact">Contact Us</Link>
              </Button>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default Services;
